/* The capability drawer (designs/intake-ui-parity.md UP4): the prototype's
   slide-in detail — submission, ML/CV block, stakeholders, tracking with the
   score strip, the delivery chain, and the discussion, plus comments.
   Tracking is edited here by an Admin with Groom… (intake-groom.md IG8),
   the one grooming editor; per-record history arrives with the activity
   read. */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import {
  DISPOSITIONS,
  EFFORTS,
  PROJECT_DISPOSITIONS,
  REVIEWER_REQUIRED_REASON,
  HEALTHS,
  JAMA_STATUSES,
  PHASE_TARGETS,
  PLATFORM_STATUSES,
  PRIORITIES,
  SCOPES,
  SUB_STATUSES,
  UNGROOMED_DISPOSITION_REASON,
  labelFor,
  type Capability,
  type CapabilityTracking,
  type PersonRef,
  type TrackingPatch,
} from "../../../shared/capabilities";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import { ENTRY_PATH_PHRASE } from "../../../shared/organizer-roles";
import type { Project } from "../../../shared/projects";
import {
  getCapabilities,
  getDetail,
  getProjects,
  isMine,
  postComment,
  postStakeholder,
  deleteStakeholder,
  postProject,
  putTracking,
  type CapabilityDetail,
  type Me,
} from "./api";
import {
  DELIVERY_STAGES,
  DELIVERY_TOTAL,
  DISPOSITION_HELP,
  DRAWER_LEAD,
  SCORE_HELP,
  UNGROOMED_NOTE,
  deliveryDone,
  ownersLabel,
} from "./board-view";
import { DeleteEffort } from "./DeleteEffort";
import { EditCapability } from "./EditCapability";
import { GroomEditor } from "./GroomEditor";
import { PersonPicker } from "./PersonPicker";

const fmtDate = (iso: string): string => iso.slice(0, 10);

/* the queue's order, as the open effort sees it (intake-drawer-stepping.md
   DS1): a null handler marks an end — the list never wraps */
export interface Stepper {
  index: number;
  total: number;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
}

/* DS3: a key typed into a field belongs to the field */
const typingIn = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement &&
  (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);

/* a section's one line on what it holds and who fills it in (QH6) */
function Lead({ children }: { children: string }): JSX.Element {
  return <p className="section-lead">{children}</p>;
}

function Section({
  title,
  lead,
  children,
}: {
  title: string;
  lead?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="drawer-section">
      <h3>{title}</h3>
      {lead !== undefined && <Lead>{lead}</Lead>}
      <div className="kv-grid">{children}</div>
    </section>
  );
}

function KV({ k, v, wide }: { k: string; v: ReactNode; wide?: boolean }): JSX.Element {
  return (
    <div className={`kv${wide ? " wide" : ""}`}>
      <div className="kv-k">{k}</div>
      <div className="kv-v">{v}</div>
    </div>
  );
}

/* a row filed under the per-domain tabs that existed before Sep 14 2026
   (intake-unified-form.md UF7): what it was filed under, shown as the
   recorded ids — honest and minimal — plus its detail block as flat
   key: value pairs. The taxonomy those ids named is deleted, so no lookup
   table is kept; the ids ARE the fact. Absent on every new ask. */
const flat = (v: unknown): string =>
  v === null || v === undefined
    ? "—"
    : Array.isArray(v)
      ? v.map(flat).join(", ")
      : typeof v === "object"
        ? Object.entries(v as Record<string, unknown>)
            .map(([k, x]) => `${k}: ${flat(x)}`)
            .join(" · ")
        : typeof v === "boolean"
          ? v
            ? "yes"
            : "no"
          : String(v);

function LegacyFiling({ c }: { c: Capability }): JSX.Element | null {
  if (c.domain === undefined || c.domain === null) return null;
  const detail = c.domainDetail ?? null;
  return (
    <Section title="Filed under (before Sep 14 2026)">
      <KV k="Domain / area" v={`${c.domain} / ${c.area ?? "—"}`} wide />
      {detail !== null &&
        Object.entries(detail).map(([k, v]) => <KV key={k} k={k} v={flat(v)} wide />)}
      <div className="kv-note" data-legacy-filing>
        Older filing categories, no longer used.
      </div>
    </Section>
  );
}
export function Drawer({
  id,
  isAdmin,
  me,
  teams,
  productLabel = null,
  stepper,
  onClose,
  onChanged,
}: {
  id: string;
  isAdmin: boolean;
  me: Me | null;
  teams: CanonicalTeam[];
  /* "Product › vehicle", resolved by the queue row (teams-and-products.md TP4) */
  productLabel?: string | null;
  /* absent when the open effort is not in the queue's current list (DS1) */
  stepper?: Stepper;
  onClose: () => void;
  /* an edit changed the record — the board behind must re-read */
  onChanged: () => void;
}): JSX.Element {
  const [detail, setDetail] = useState<CapabilityDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  /* grooming in place (intake-groom.md IG8) */
  const [grooming, setGrooming] = useState(false);
  /* a team's +1 from here (intake-flow-review.md FR1) */
  const [backing, setBacking] = useState(false);
  const [backTeam, setBackTeam] = useState("");
  const [backNote, setBackNote] = useState("");
  const [backBusy, setBackBusy] = useState(false);
  const [backError, setBackError] = useState<string | null>(null);
  /* FR5: an Admin's withdrawal, confirmed on the card */
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [chainBusy, setChainBusy] = useState(false);
  const [chainError, setChainError] = useState<string | null>(null);

  /* one appended tracking revision per chain change (IDM9). The reload is
     AWAITED (PR-100 review): the controls stay disabled until the fresh
     detail renders, so a second click can never act on stale checkboxes
     and mint a duplicate revision. */
  const putChain = async (patch: TrackingPatch): Promise<void> => {
    setChainBusy(true);
    setChainError(null);
    try {
      await putTracking(id, patch);
      await load();
      onChanged();
    } catch (e) {
      setChainError(e instanceof Error ? e.message : String(e));
    } finally {
      setChainBusy(false);
    }
  };

  /* the disposition chooser (intake-project-mapping.md IPM6) — admin-only,
     same authority path as the chain above. The project list is fetched
     lazily: only once something actually needs a project NAME (the
     chooser is open, or a mapped ask needs its linked project's name),
     never on every drawer open. */
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [projectsFailed, setProjectsFailed] = useState(false);
  const loadProjects = useCallback((): void => {
    if (projects !== null || projectsFailed) return;
    getProjects()
      .then(setProjects)
      .catch(() => setProjectsFailed(true));
  }, [projects, projectsFailed]);

  const [choosing, setChoosing] = useState(false);
  const [pickedKind, setPickedKind] = useState<string | null>(null);
  const [pickedProjectId, setPickedProjectId] = useState("");
  const [newName, setNewName] = useState("");
  /* the register's outcomes (UF17/UF18/UF19): a reviewer for "team builds
     it; platform reviews", and the effort a duplicate points at */
  const [pickedReviewer, setPickedReviewer] = useState<PersonRef | null>(null);
  const [pickedDupId, setPickedDupId] = useState("");
  const [others, setOthers] = useState<Capability[] | null>(null);
  /* the effort this drawer is on NOW — a response for one stepped past is
     dropped, never committed under the new effort (Copilot on #151) */
  const currentId = useRef(id);
  currentId.current = id;
  const loadOthers = useCallback((): void => {
    if (others !== null) return;
    getCapabilities()
      .then((list) => {
        if (currentId.current === id) setOthers(list.filter((c) => c.id !== id && c.archived !== true));
      })
      .catch(() => {
        if (currentId.current === id) setOthers([]);
      });
  }, [others, id]);
  const [dispBusy, setDispBusy] = useState(false);
  const [dispError, setDispError] = useState<string | null>(null);

  const openChooser = (current: CapabilityTracking | null, preset?: string): void => {
    setChoosing(true);
    setPickedKind(preset ?? current?.disposition ?? null);
    setPickedProjectId(current?.projectId ?? "");
    setPickedReviewer(current?.reviewer ?? null);
    setPickedDupId(current?.duplicateOfId ?? "");
    setNewName("");
    setDispError(null);
    loadProjects();
    loadOthers();
  };

  const saveDisposition = async (): Promise<void> => {
    if (pickedKind === null) return void setDispError("pick an outcome");
    /* pre-check (D8 idiom): the server enforces this too, but checking
       here means a doomed New-project attempt never creates an orphaned
       draft project before finding out the tracking write would refuse it */
    if (detail?.tracking?.groomed !== true) return void setDispError(UNGROOMED_DISPOSITION_REASON);
    if (pickedKind === "team_builds_platform_reviews" && pickedReviewer === null)
      return void setDispError(REVIEWER_REQUIRED_REASON);
    if (pickedKind === "duplicate_of" && pickedDupId === "")
      return void setDispError("pick the effort this duplicates");
    setDispBusy(true);
    setDispError(null);
    try {
      if (pickedKind === "duplicate_of") {
        /* UF18/UF19: one save — the server records the link, the +1 onto
           the survivor, and folds this effort (archives it) */
        await putTracking(id, {
          disposition: "duplicate_of",
          projectId: null,
          duplicateOfId: pickedDupId,
          reviewer: pickedReviewer,
        });
        await load();
        onChanged();
        setChoosing(false);
        return;
      }
      let projectId = pickedProjectId;
      if (pickedKind === "new_project") {
        if (newName.trim() === "") {
          setDispBusy(false);
          setDispError("a name is required");
          return;
        }
        const created = await postProject(newName.trim());
        projectId = created.id;
        /* the just-created project can't be in a list fetched before it
           existed — append it locally rather than trust a stale cache
           (or force a re-fetch) so the read view can resolve its name */
        setProjects((prev) => (prev === null ? [created] : [...prev, created]));
      } else if (projectId === "") {
        setDispBusy(false);
        setDispError("pick a project");
        return;
      }
      await putTracking(id, {
        disposition: pickedKind,
        projectId,
        duplicateOfId: null,
        reviewer: pickedReviewer,
      });
      await load();
      onChanged();
      setChoosing(false);
    } catch (e) {
      setDispError(e instanceof Error ? e.message : String(e));
    } finally {
      setDispBusy(false);
    }
  };

  const clearDisposition = async (): Promise<void> => {
    setDispBusy(true);
    setDispError(null);
    try {
      await putTracking(id, { disposition: null, projectId: null, duplicateOfId: null });
      await load();
      onChanged();
      setChoosing(false);
    } catch (e) {
      setDispError(e instanceof Error ? e.message : String(e));
    } finally {
      setDispBusy(false);
    }
  };

  const projectName = (pid: string): string => {
    if (projectsFailed) return "(project name unavailable)";
    if (projects === null) return "loading…";
    return projects.find((p) => p.id === pid)?.name ?? "a project that no longer exists";
  };

  /* resizable width — drag the left edge; remembered across opens (the
     prototype's affordance, restored per Lewis Aug 31). localStorage is a
     per-viewer convenience: absent or refused, the CSS default width holds. */
  const [width, setWidth] = useState<number | null>(() => {
    try {
      const stored = localStorage.getItem("intake-drawer-width");
      return stored === null ? null : Number(stored);
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (width === null) return;
    try {
      localStorage.setItem("intake-drawer-width", String(width));
    } catch {
      /* private mode etc. — the drag still works for this open */
    }
  }, [width]);
  const startResize = (e: ReactMouseEvent): void => {
    e.preventDefault();
    const onMove = (ev: MouseEvent): void => {
      /* ceiling first, floor last — the 400px minimum always wins, even in
         a degenerate viewport */
      setWidth(Math.max(400, Math.min(window.innerWidth - ev.clientX, window.innerWidth - 40)));
    };
    const onUp = (): void => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.userSelect = "";
    };
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const load = useCallback(
    (): Promise<void> =>
      getDetail(id)
        .then((d) => {
          if (currentId.current === id) setDetail(d);
        })
        .catch((e: unknown) => {
          if (currentId.current === id) setError(e instanceof Error ? e.message : String(e));
        }),
    [id],
  );
  useEffect(() => {
    /* a different row is a different record — nothing carries over */
    setDetail(null);
    setError(null);
    setComment("");
    setChoosing(false);
    /* the duplicate chooser's list excludes THIS effort — another effort's
       list would offer the effort itself (Copilot on #151) */
    setOthers(null);
    setGrooming(false);
    setBacking(false);
    setBackTeam("");
    setBackNote("");
    setBackError(null);
    setWithdrawing(null);
    setWithdrawError(null);
    setProjects(null);
    setProjectsFailed(false);
    void load();
  }, [load]);

  /* the READ view needs the linked project's NAME the moment a mapped ask
     loads, whether or not the chooser is ever opened — loadProjects is a
     no-op once fetched (or failed), so this never re-fetches on every
     render */
  useEffect(() => {
    if (detail?.tracking?.projectId != null) loadProjects();
  }, [detail, loadProjects]);

  /* the prototype's escape hatch */
  useEffect(() => {
    const h = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  /* DS4: a step never throws away work in progress */
  const stepBlocked: string | null = editing
    ? "Finish or cancel the edit first."
    : grooming
      ? "Save or cancel the grooming first."
      : backing
        ? "Add or cancel your +1 first."
        : choosing
      ? "Save or cancel the outcome first."
      : comment.trim() !== ""
        ? "Send or clear your comment first."
        : null;
  const goPrev = stepBlocked === null ? (stepper?.onPrev ?? null) : null;
  const goNext = stepBlocked === null ? (stepper?.onNext ?? null) : null;

  /* DS3: ↑/↓ and k/j step, unless a field has the keys or a modifier is held */
  useEffect(() => {
    const h = (e: KeyboardEvent): void => {
      if (e.altKey || e.ctrlKey || e.metaKey || typingIn(e.target)) return;
      const go = e.key === "ArrowUp" || e.key === "k" ? goPrev : e.key === "ArrowDown" || e.key === "j" ? goNext : null;
      if (go === null) return;
      e.preventDefault();
      go();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [goPrev, goNext]);

  /* DS2: a step lands at the top of the next record */
  const asideRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (asideRef.current !== null) asideRef.current.scrollTop = 0;
  }, [id]);

  const addBacking = async (): Promise<void> => {
    if (backTeam === "") return void setBackError("pick your team — a +1 is your team's");
    setBackBusy(true);
    setBackError(null);
    try {
      await postStakeholder(id, backTeam, backNote.trim() === "" ? null : backNote.trim());
      setBacking(false);
      setBackTeam("");
      setBackNote("");
      void load();
      onChanged();
    } catch (e) {
      setBackError(e instanceof Error ? e.message : String(e));
    } finally {
      setBackBusy(false);
    }
  };

  const withdraw = async (teamId: string): Promise<void> => {
    setWithdrawBusy(true);
    setWithdrawError(null);
    try {
      await deleteStakeholder(id, teamId);
      setWithdrawing(null);
      void load();
      onChanged();
    } catch (e) {
      setWithdrawError(e instanceof Error ? e.message : String(e));
    } finally {
      setWithdrawBusy(false);
    }
  };

  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await postComment(id, comment);
      setComment("");
      void load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const body = (): JSX.Element => {
    if (detail === null)
      return error !== null ? (
        <p className="capture-error">{error}</p>
      ) : (
        <div className="loading">Loading…</div>
      );

    const { current: c, origin, stakeholders, comments, tracking: t, score } = detail;
    const reach = 1 + stakeholders.length + (c.scope === "common" ? 1 : 0);
    /* a team by NAME, its id only when it is not on the list (FR5) */
    const teamName = (tid: string): string => teams.find((tm) => tm.id === tid)?.name ?? tid;
    /* Admin, or the ORIGINAL SUBMITTER of this ask (IDM4-a) */
    const canEdit = isAdmin || isMine(me, origin.recorded.by?.userId);
    return (
      <>
        <div className="drawer-head">
          {t?.health != null && (
            <span className={`health-dot ${t.health}`} title={labelFor(HEALTHS, t.health)} />
          )}
          <h2>{c.name}</h2>
          {canEdit && !editing && (
            <button
              className="capture-notdup drawer-edit"
              title={
                isAdmin
                  ? "Edit every field of this effort (Admin)."
                  : "You submitted this effort — edit its fields."
              }
              onClick={() => setEditing(true)}
            >
              Edit
            </button>
          )}
          <div className="drawer-sub">
            {c.teamId} · {origin.recorded.by?.name ?? origin.recorded.source}
            {origin.recorded.via !== undefined && ` ${ENTRY_PATH_PHRASE[origin.recorded.via]}`} ·{" "}
            {fmtDate(origin.recorded.date)}
            {stakeholders.length > 0 &&
              ` · +${stakeholders.length} team${stakeholders.length > 1 ? "s" : ""}`}
          </div>
        </div>

        {editing && (
          <EditCapability
            current={c}
            teams={teams}
            isAdmin={isAdmin}
            onCancel={() => setEditing(false)}
            onSaved={() => {
              setEditing(false);
              load();
              onChanged();
            }}
          />
        )}
        {!editing && (
        <>

        <Section title="Submission" lead={DRAWER_LEAD.submission}>
          <KV k="What it needs to do" v={c.description ?? "—"} wide />
          <KV k="Scope" v={labelFor(SCOPES, c.scope)} />
          <KV k="Priority" v={labelFor(PRIORITIES, c.priority)} />
          <KV k="Submitter status" v={labelFor(SUB_STATUSES, c.subStatus)} />
          <KV k="Needed by" v={c.neededBy ?? "—"} />
          <KV k="Requested phase" v={labelFor(PHASE_TARGETS, c.phaseTarget)} />
          <KV k="Jama" v={labelFor(JAMA_STATUSES, c.jamaStatus)} />
          {/* always shown, because an unanswered ownership question is itself
              worth seeing on the board's biggest open asks (OM2/OM7). The
              three states read as three different sentences, never one
              collapsed "—" that would hide which is which. */}
          <KV
            k="Expected delivery owners"
            v={ownersLabel(c.owners == null ? null : c.owners.map((id) => teams.find((t) => t.id === id)?.name ?? id))}
            wide
          />
          <KV k="Product" v={productLabel ?? c.productId ?? "—"} />
          <KV
            k="Point of contact"
            v={
              c.pointOfContact == null ? (
                <span className="not-recorded" data-contact-state="missing">
                  Not recorded
                </span>
              ) : (
                c.pointOfContact.name
              )
            }
          />
          {c.jamaLink !== null && <KV k="Jama link" v={c.jamaLink} />}
          {c.dependencies !== null && <KV k="Dependencies / needs from Platform" v={c.dependencies} wide />}
        </Section>

        <LegacyFiling c={c} />

        {/* FR1 + FR5: the +1s as one idea — the reach they make, each team's
            +1 as a card, an Admin's Withdraw, and the way to add your own */}
        <section className="drawer-section" data-plus-one>
          <h3>+1s — teams that need this too ({stakeholders.length})</h3>
          <p className="plus-one-reach" data-plus-one-reach>
            Reach {reach}: {teamName(c.teamId)} registered it
            {stakeholders.length > 0 &&
              `, ${stakeholders.length} other team${stakeholders.length === 1 ? " needs" : "s need"} it too`}
            {c.scope === "common" && ", +1 for being common to several vehicles"}.
          </p>
          <Lead>
            A +1 is another team saying it needs this effort too. Each one raises its reach and its
            score, so work several teams need is built once and ranks higher.
          </Lead>
          {stakeholders.length === 0 ? (
            <p className="kv-note">No other team has added a +1 yet.</p>
          ) : (
            <ul className="plus-one-list">
              {stakeholders.map((s) => (
                <li key={s.teamId} className="plus-one-card">
                  <div className="plus-one-head">
                    <span className="plus-one-badge">+1</span>
                    <b className="plus-one-team">{teamName(s.teamId)}</b>
                    {isAdmin && withdrawing !== s.teamId && (
                      <button
                        className="btn small ghost plus-one-withdraw"
                        title="Withdraw this +1 — an Admin correction; the record keeps that it was there"
                        onClick={() => {
                          setWithdrawing(s.teamId);
                          setWithdrawError(null);
                        }}
                      >
                        Withdraw
                      </button>
                    )}
                  </div>
                  {s.note !== null && <p className="plus-one-note">“{s.note}”</p>}
                  <p className="plus-one-stamp">
                    added{s.recorded.by != null && <> by {s.recorded.by.name}</>}
                    {s.recorded.via !== undefined && <> {ENTRY_PATH_PHRASE[s.recorded.via]}</>} ·{" "}
                    {fmtDate(s.recorded.date)}
                  </p>
                  {withdrawing === s.teamId && (
                    <div className="plus-one-confirm">
                      <span>Withdraw {teamName(s.teamId)}&apos;s +1?</span>
                      <button
                        className="btn small primary"
                        disabled={withdrawBusy}
                        onClick={() => void withdraw(s.teamId)}
                      >
                        {withdrawBusy ? "Withdrawing…" : "Withdraw"}
                      </button>
                      <button
                        className="btn small ghost"
                        disabled={withdrawBusy}
                        onClick={() => setWithdrawing(null)}
                      >
                        Keep
                      </button>
                      {withdrawError !== null && <p className="capture-error">{withdrawError}</p>}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {!backing ? (
            <div className="form-actions">
              <button className="btn small ghost" onClick={() => setBacking(true)}>
                +1 for my team…
              </button>
            </div>
          ) : (
            <div className="plus-one-form">
              <label className="capture-field">
                <span>Your team</span>
                <select value={backTeam} disabled={backBusy} onChange={(e) => setBackTeam(e.target.value)}>
                  <option value="">— pick your team —</option>
                  {teams
                    .filter(
                      (tm) =>
                        tm.retired !== true &&
                        tm.id !== c.teamId &&
                        !stakeholders.some((s) => s.teamId === tm.id),
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((tm) => (
                      <option key={tm.id} value={tm.id}>
                        {tm.name}
                      </option>
                    ))}
                </select>
              </label>
              <label className="capture-field">
                <span>What your team needs from it</span>
                <input
                  value={backNote}
                  disabled={backBusy}
                  placeholder="Optional"
                  onChange={(e) => setBackNote(e.target.value)}
                />
              </label>
              {backError !== null && <p className="capture-error">{backError}</p>}
              <div className="form-actions">
                <button className="btn small primary" disabled={backBusy} onClick={() => void addBacking()}>
                  {backBusy ? "Adding…" : "Add our +1"}
                </button>
                <button
                  className="btn small ghost"
                  disabled={backBusy}
                  onClick={() => {
                    setBacking(false);
                    setBackError(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>

        <section className="drawer-section">
          <h3>Platform tracking</h3>
          <Lead>{DRAWER_LEAD.tracking}</Lead>
          <div className="score-strip" title={SCORE_HELP}>
            <span className="score-big">{score}</span>
            <div className="score-meta">
              <div>
                priority score — <b>value × reach ÷ effort</b>, higher = do sooner
              </div>
              <div>
                reach {reach} team{reach > 1 ? "s" : ""} · effort{" "}
                {t?.effort != null ? labelFor(EFFORTS, t.effort) : "unset (assumed M)"}
                {(t?.committed ?? false) && <span className="committed-pill">Committed</span>}
              </div>
            </div>
          </div>
          {t === null ? (
            <div className="kv-note">{UNGROOMED_NOTE}</div>
          ) : (
            <div className="kv-grid">
              <KV k="Status" v={labelFor(PLATFORM_STATUSES, t.platformStatus)} />
              <KV k="Health" v={t.health != null ? labelFor(HEALTHS, t.health) : "—"} />
              <KV
                k="Roadmap phase"
                v={
                  t.roadmapPhase != null
                    ? labelFor(PHASE_TARGETS, t.roadmapPhase) +
                      (t.roadmapPhase !== c.phaseTarget
                        ? ` (changed from requested: ${labelFor(PHASE_TARGETS, c.phaseTarget)})`
                        : "")
                    : `— (using requested: ${labelFor(PHASE_TARGETS, c.phaseTarget)})`
                }
              />
              <KV k="Planned start" v={t.plannedStart ?? "—"} />
              <KV k="Planned end" v={t.plannedEnd ?? "—"} />
              <KV k="Platform owner" v={t.platformOwner ?? "—"} />
              <KV k="Requesting team owner" v={t.teamOwner ?? "—"} />
              <KV k="Committed" v={t.committed ? "Yes — this cycle" : "Backlog"} />
              <KV k="Jira epic" v={t.jiraEpic ?? "—"} />
              {t.platformNotes !== null && <KV k="Notes" v={t.platformNotes} wide />}
            </div>
          )}
          {/* IG8: grooming is edited here, on the queue and the board alike */}
          {isAdmin ? (
            grooming ? (
              <GroomEditor
                row={{ capability: c, tracking: t, stakeholderCount: stakeholders.length, score }}
                title="Grooming"
                teams={teams}
                onClose={() => setGrooming(false)}
                onSaved={() => {
                  setGrooming(false);
                  void load();
                  onChanged();
                }}
              />
            ) : (
              <div className="form-actions">
                <button className="btn small ghost" onClick={() => setGrooming(true)}>
                  Groom…
                </button>
              </div>
            )
          ) : (
            <div className="kv-note">Only an intake manager (Admin) can edit tracking.</div>
          )}
        </section>

        <section className="drawer-section">
          <h3>Disposition</h3>
          <Lead>{DRAWER_LEAD.disposition}</Lead>
          {!choosing ? (
            <>
              {t?.disposition == null ? (
                <div className="kv-note">Not yet decided.</div>
              ) : t.disposition === "duplicate_of" ? (
                <div className="kv-note" data-disposition-read>
                  {labelFor(DISPOSITIONS, t.disposition)} →{" "}
                  {others?.find((o) => o.id === t.duplicateOfId)?.name ?? t.duplicateOfId}
                </div>
              ) : (
                <div className="kv-note" data-disposition-read>
                  {labelFor(DISPOSITIONS, t.disposition)} → {projectName(t.projectId!)}
                </div>
              )}
              {/* UF17: the platform person in the team's designs and reviews —
                  red when the outcome demands one and nobody is named */}
              {t?.disposition === "team_builds_platform_reviews" && (
                <div
                  className={t.reviewer === null ? "kv-note not-recorded" : "kv-note"}
                  data-reviewer-state={t.reviewer === null ? "missing" : "named"}
                >
                  Platform reviewer: {t.reviewer === null ? "not recorded" : t.reviewer.name}
                </div>
              )}
              {isAdmin && (
                <div className="form-actions">
                  {/* FR2: the gate shows before the click; the save-time
                      check stays as the backstop */}
                  <button
                    className="btn small ghost"
                    disabled={t?.groomed !== true}
                    title={t?.groomed !== true ? UNGROOMED_DISPOSITION_REASON : undefined}
                    onClick={() => openChooser(t)}
                  >
                    {t?.disposition == null ? "Decide the outcome…" : "Change…"}
                  </button>
                  {/* UF19: Admin-only — the same act as the duplicate outcome */}
                  {t?.disposition !== "duplicate_of" && (
                    <button
                      className="btn small ghost"
                      data-same-as
                      disabled={t?.groomed !== true}
                      title={t?.groomed !== true ? UNGROOMED_DISPOSITION_REASON : undefined}
                      onClick={() => openChooser(t, "duplicate_of")}
                    >
                      Same as…
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="disposition-chooser">
              {DISPOSITIONS.map((d) => (
                <label key={d.value} className="disposition-option" title={DISPOSITION_HELP[d.value]}>
                  <input
                    type="radio"
                    name="disposition"
                    checked={pickedKind === d.value}
                    disabled={dispBusy}
                    onChange={() => setPickedKind(d.value)}
                  />
                  {d.label}
                </label>
              ))}
              {pickedKind === "duplicate_of" ? (
                others === null ? (
                  <div className="kv-note">Loading efforts…</div>
                ) : (
                  <select
                    aria-label="Pick the effort this duplicates"
                    value={pickedDupId}
                    disabled={dispBusy}
                    onChange={(e) => setPickedDupId(e.target.value)}
                  >
                    <option value="">— pick the effort this duplicates —</option>
                    {others.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                )
              ) : pickedKind === "new_project" ? (
                <input
                  aria-label="New project name"
                  placeholder="New project name"
                  value={newName}
                  disabled={dispBusy}
                  onChange={(e) => setNewName(e.target.value)}
                />
              ) : pickedKind !== null && PROJECT_DISPOSITIONS.includes(pickedKind) ? (
                projects === null && !projectsFailed ? (
                  <div className="kv-note">Loading projects…</div>
                ) : (
                  <select
                    aria-label="Pick a project"
                    value={pickedProjectId}
                    disabled={dispBusy}
                    onChange={(e) => setPickedProjectId(e.target.value)}
                  >
                    <option value="">— pick a project —</option>
                    {(projects ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )
              ) : null}
              {pickedKind === "team_builds_platform_reviews" && (
                <PersonPicker
                  label="Platform reviewer"
                  value={pickedReviewer}
                  onChange={setPickedReviewer}
                  missing="Required for this outcome — name the platform reviewer."
                  disabled={dispBusy}
                />
              )}
              {dispError !== null && <p className="capture-error">{dispError}</p>}
              <div className="form-actions">
                <button
                  className="btn small primary"
                  disabled={dispBusy}
                  onClick={() => void saveDisposition()}
                >
                  {dispBusy ? "Saving…" : "Save disposition"}
                </button>
                <button className="btn small ghost" disabled={dispBusy} onClick={() => setChoosing(false)}>
                  Cancel
                </button>
                {t?.disposition != null && (
                  <button
                    className="btn small ghost"
                    disabled={dispBusy}
                    onClick={() => void clearDisposition()}
                  >
                    Clear disposition
                  </button>
                )}
              </div>
            </div>
          )}
        </section>

        <section className="drawer-section">
          <h3>Delivery chain</h3>
          <Lead>{DRAWER_LEAD.delivery}</Lead>
          <div className="delivery-progress">
            <div className="delivery-bar">
              <div
                className="delivery-fill"
                style={{ width: `${(deliveryDone(t) / DELIVERY_TOTAL) * 100}%` }}
              />
            </div>
            <span className="delivery-count">
              {deliveryDone(t)} / {DELIVERY_TOTAL}
            </span>
          </div>
          <ul className="delivery-list">
            {DELIVERY_STAGES.map((s) => {
              const on = t?.[s.key] ?? false;
              return (
                <li key={s.key} className={on ? "on" : ""}>
                  {isAdmin ? (
                    /* the chain edits where it lives (IDM9): one appended
                       tracking revision per toggle */
                    <label>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={chainBusy}
                        onChange={(e) => void putChain({ [s.key]: e.target.checked })}
                      />
                      {s.label}
                    </label>
                  ) : (
                    <span>
                      <span className="dl-mark">{on ? "✓" : "○"}</span> {s.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="charter-link-row">
            <span className="kv-k">Charter link</span>
            {isAdmin ? (
              <input
                key={t?.charterLink ?? ""}
                className="cell-edit"
                defaultValue={t?.charterLink ?? ""}
                placeholder="https:// … the charter document"
                aria-label="Charter link"
                disabled={chainBusy}
                onBlur={(e) => {
                  const next = e.target.value.trim();
                  if (next !== (t?.charterLink ?? ""))
                    void putChain({ charterLink: next === "" ? null : next });
                }}
              />
            ) : t?.charterLink != null ? (
              /* only http(s) becomes a clickable anchor (PR-100 review) —
                 anything else renders as inert text, never as a link */
              /^https?:\/\//i.test(t.charterLink) ? (
                <a href={t.charterLink} target="_blank" rel="noopener noreferrer">
                  {t.charterLink}
                </a>
              ) : (
                <span className="kv-v">{t.charterLink}</span>
              )
            ) : (
              <span className="kv-note">not recorded</span>
            )}
          </div>
          {chainError !== null && <p className="capture-error">{chainError}</p>}
        </section>

        <p className="intake-drawer-origin">
          Original ask: “{origin.name}” — {labelFor(PRIORITIES, origin.priority)},{" "}
          {origin.recorded.by?.name ?? origin.recorded.source}, {fmtDate(origin.recorded.date)}.
          Revision 1 survives every edit.
        </p>
        </>
        )}

        <section className="drawer-section">
          <h3>Discussion ({comments.length})</h3>
          {comments.length === 0 ? (
            <div className="kv-note">
              No comments yet — start the conversation here so it lives with the capability.
            </div>
          ) : (
            <ul className="comments">
              {comments.map((m) => (
                <li key={m.id}>
                  <div className="c-head">
                    <b>{m.recorded.by?.name ?? m.recorded.source}</b>
                    <span className="c-time">{fmtDate(m.recorded.date)}</span>
                  </div>
                  <div className="c-body">{m.body}</div>
                </li>
              ))}
            </ul>
          )}
          <div className="comment-compose">
            <textarea
              rows={2}
              placeholder="Add a comment…"
              aria-label="Add a comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="compose-actions">
              <button
                className="btn small"
                disabled={busy || comment.trim() === ""}
                onClick={() => void send()}
              >
                {busy ? "Posting…" : "Comment"}
              </button>
            </div>
            {error !== null && <p className="capture-error">{error}</p>}
          </div>
        </section>
        {/* last on the record and apart from Edit/archive — archive keeps,
            delete removes (intake-delete.md ID1) */}
        {isAdmin && !editing && (
          <DeleteEffort
            id={c.id}
            name={c.name}
            onDeleted={() => {
              onChanged();
              onClose();
            }}
          />
        )}
      </>
    );
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside
        ref={asideRef}
        className="drawer"
        data-intake-drawer
        style={width !== null ? { width: `${width}px` } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer-resize" onMouseDown={startResize} title="Drag to resize" />
        <button className="drawer-close" onClick={onClose} aria-label="Close record">
          ✕
        </button>
        {stepper !== undefined && (
          <div className="drawer-stepper">
            <button
              className="step-btn"
              aria-label="Previous effort"
              title={stepBlocked ?? (stepper.onPrev === null ? "This is the first effort in the list." : "Previous effort (↑ or k)")}
              disabled={goPrev === null}
              onClick={() => goPrev?.()}
            >
              ↑
            </button>
            <span className="step-pos" data-drawer-position>
              {stepper.index + 1} of {stepper.total}
            </span>
            <button
              className="step-btn"
              aria-label="Next effort"
              title={stepBlocked ?? (stepper.onNext === null ? "This is the last effort in the list." : "Next effort (↓ or j)")}
              disabled={goNext === null}
              onClick={() => goNext?.()}
            >
              ↓
            </button>
          </div>
        )}
        {body()}
      </aside>
    </div>
  );
}
